import React, { useMemo } from "react";
import Card from "../../features/ui/Card";
import ProOverlay from "./ProOverlay";
import Spinner from "./Spinner";

function formatPct(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)}%`;
}

export default function TargetGradeCard({
  minGrade,
  targetGrade,
  onChangeTarget,
  onCommitTarget,
  helperText,
  analysis,
  analysisError,

  analysisLoading = false,

  locked = false,
  lockText = "Upgrade to Pro to unlock this feature.",
  onRequestPro,
}) {
  const isTargetsMode =
    analysis &&
    typeof analysis === "object" &&
    analysis.mode === "targets" &&
    Array.isArray(analysis.rows);

  const wantedRow = useMemo(() => {
    if (!isTargetsMode) return null;
    return analysis.rows.find((r) => r?.isWanted) || null;
  }, [isTargetsMode, analysis]);

  const fallbackText = analysisError
    ? analysisError
    : analysis?.message
    ? analysis.message
    : "";

  const disabled = Boolean(locked || analysisLoading);

  return (
    <Card className="relative p-6">
      {/* Loading overlay (relative to this card) */}
      {analysisLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/60 dark:bg-neutral-950/60">
          <div className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-700 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200">
            <Spinner size={16} />
            Analysing…
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">
          Target Grade Analysis
        </h3>

        {/* Optional badge (keep or remove; overlay already shows) */}
        {!analysisLoading ? null : (
          <div className="sr-only" aria-live="polite">
            Analysing…
          </div>
        )}
      </div>

      {/* Content (blurred when locked) */}
      <div
        className={
          locked
            ? "mt-14 select-none blur-[2px] opacity-60 pointer-events-none"
            : "mt-4"
        }
      >
        <div className="text-center text-sm text-neutral-600 dark:text-neutral-400">
          I want a{" "}
          <span className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
            {targetGrade}%
          </span>
        </div>

        <div className="mt-3">
          <input
            type="range"
            min={minGrade}
            max={100}
            value={targetGrade}
            disabled={disabled}
            onChange={(e) => onChangeTarget(Number(e.target.value))}
            // pointer events can miss on some devices; add onMouseUp/onTouchEnd too
            onPointerUp={(e) => onCommitTarget?.(Number(e.target.value))}
            onMouseUp={(e) => onCommitTarget?.(Number(e.target.value))}
            onTouchEnd={(e) => onCommitTarget?.(Number(e.target.value))}
            className={disabled ? "w-full opacity-60" : "w-full"}
          />
        </div>

        {isTargetsMode ? (
          <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                Required average (remaining)
              </div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                Targets: {analysis.rows.length}
              </div>
            </div>

            <div className="grid grid-cols-[110px_1fr] gap-x-4 gap-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Target
              </div>
              <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Needed avg
              </div>

              {analysis.rows.map((r) => {
                const isWanted = Boolean(r?.isWanted);
                const target = r?.target;
                const req = r?.requiredAverage;

                return (
                  <React.Fragment key={String(target)}>
                    <div className="flex items-center gap-2">
                      <span
                        className={
                          isWanted
                            ? "font-semibold text-neutral-900 dark:text-neutral-50"
                            : "font-medium text-neutral-900 dark:text-neutral-100"
                        }
                      >
                        {Number.isFinite(Number(target)) ? `${target}%` : "—"}
                      </span>

                      {isWanted ? (
                        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                          Wanted
                        </span>
                      ) : null}
                    </div>

                    <div
                      className={
                        isWanted
                          ? "font-semibold text-neutral-900 dark:text-neutral-50"
                          : "text-neutral-800 dark:text-neutral-200"
                      }
                    >
                      {formatPct(req)}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {wantedRow?.message ? (
              <div className="mt-3 text-xs text-neutral-600 dark:text-neutral-400">
                {wantedRow.message}
              </div>
            ) : null}

            {analysisError ? (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
                {analysisError}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
            {fallbackText || helperText}
          </div>
        )}
      </div>

      {locked ? <ProOverlay text={lockText} onClick={onRequestPro} /> : null}
    </Card>
  );
}